import { useState, useRef, useEffect, useCallback } from 'react';
import { useLoginForm } from '../../hooks/useLoginForm';
import PasswordInput from './PasswordInput';
import EmailInput from './EmailInput';
import AuthImage from './AuthImage';
import LogoBlue from '../../assets/images/Logo-Blue.png';
import Logo from '../../components/common/Logo';
import ErrorMessage from '../../components/common/ErrorMessage';
import Checkbox from '../../components/common/Checkbox';
import { PrimaryButton } from '../../components/common/ButtonsV2';

/**
 * LoginForm.jsx
 *
 * Presentational component for the login form.
 * It contains input fields, local validation, and UI behaviors.
 * Props:
 * - onLogin: function → called with { email, password, rememberMe } on valid submit
 * - isLoading: boolean → disables button and shows loader text
 * - serverError: string → optional backend error to display
 */
const LoginForm = ({ onLogin, isLoading, serverError }) => {
  const {
    email,
    password,
    emailError,
    passwordError,
    emailErrorRef,
    passwordErrorRef,
    handleEmailChange,
    handleEmailBlur,
    handlePasswordChange,
    validateForm,
    isLoginReady,
  } = useLoginForm();

  const [passwordVisible, setPasswordVisible] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  const emailInputRef = useRef(null);

  useEffect(() => {
    emailInputRef.current?.focus();
  }, []);

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();

      if (!validateForm()) {
        if (emailError) {
          emailErrorRef.current?.focus();
        } else if (passwordError) {
          passwordErrorRef.current?.focus();
        }
        return;
      }

      onLogin({ email, password, rememberMe });
    },
    [
      validateForm,
      emailError,
      passwordError,
      emailErrorRef, // ← añadidos
      passwordErrorRef, // ← añadidos
      onLogin,
      email,
      password,
      rememberMe,
    ],
  );

  const togglePasswordVisibility = () => setPasswordVisible((v) => !v);

  return (
    <div className="h-screen w-screen bg-custom-light-gray flex overflow-auto">
      <div className="flex-1 lg:grid lg:grid-cols-2 lg:gap-0 h-full">
        <AuthImage />

        <div className="flex flex-col items-center justify-center p-3 sm:p-4 md:p-6">
          <div className="w-full max-w-xs mx-auto">
            <Logo
              URL={LogoBlue}
              name="Janway Logo"
              variant="small"
              clickable={false}
              className="mb-4 mx-auto"
            />
          </div>

          <div className="w-full max-w-[450px]">
            <h2 className="text-xl sm:text-2xl lg:text-3xl text-black font-bold text-center mb-6">
              Welcome Back
            </h2>

            <form onSubmit={handleSubmit} className="space-y-2 sm:space-y-3">
              <EmailInput
                value={email}
                onChange={handleEmailChange}
                onBlur={handleEmailBlur}
                error={emailError}
                ref={emailInputRef}
                errorRef={emailErrorRef}
              />

              <PasswordInput
                value={password}
                onChange={handlePasswordChange}
                error={passwordError}
                errorRef={passwordErrorRef}
                visible={passwordVisible}
                onToggleVisibility={togglePasswordVisibility}
              />

              <div className="flex items-center justify-between">
                <label className="flex items-center text-sm text-black gap-2">
                  <Checkbox checked={rememberMe} onChange={setRememberMe} />
                  Remember me
                </label>
                <button type="button" className="text-sm text-black">
                  Forgot password?
                </button>
              </div>

              <div className="space-y-4 pt-4">
                <PrimaryButton type="submit" disabled={isLoading || !isLoginReady} fullWidth>
                  {isLoading ? 'Logging in...' : 'Login'}
                </PrimaryButton>
              </div>

              <ErrorMessage message={serverError} className="text-center mt-4 !static w-full" />
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;
